import { CheckboxCell, CheckboxHeader, TextCell, TextWithHoverAction } from '@/components/tables/contentFields';
import EmptyTableView from '@/components/tables/EmptyTableView';
import { NonSortableColumnHeader } from '@/components/tables/NonSortableColumnHeader';
import Table, { TableBody, TableHeader } from '@/components/tables/TableBasics';
import TableLoadingView from '@/components/tables/TableLoadingView';
import { useDeviceModels } from '@/features/device-models';
import { formatDate, formatLastActive, formatSecondsAsString } from '@/utils/dateUtils';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Device } from '../types';
import { StatusLabel } from './StatusLabel';

interface DevicesTableProps {
  devices: Device[];
  isLoading: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onRowClick: (device: Device) => void;
  // Optional: when omitted the company column stays plain text instead of a link.
  onCompanyClick?: (company: { id: string; name: string }) => void;
}


export function DevicesTable({
  devices,
  isLoading,
  selectedIds,
  onSelectionChange,
  onRowClick,
  onCompanyClick,
}: DevicesTableProps) {
  const { t } = useTranslation();
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const { data: deviceTypes } = useDeviceModels();

  const deviceTypeMap = useMemo(() => {
    if (!deviceTypes) return new Map<number, string>();
    return new Map(deviceTypes.map((dt) => [dt.id, dt.name]));
  }, [deviceTypes]);

  const allSelected =
    devices.length > 0 && selectedIds.size === devices.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(devices.map((d) => d.id)));
    }
  };

  const handleSelectRow = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    onSelectionChange(newSelection);
  };

  if (isLoading && devices.length === 0) {
    return (
      <TableLoadingView isLoading={isLoading} rows={5} columns={8} withHeader={true} />
    );
  }

  if (devices.length === 0) {
    return (
      <EmptyTableView />
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <CheckboxHeader
            checked={allSelected}
            onChange={handleSelectAll}
            ariaLabel={t('devices.table.selectAll')}
            indeterminate={someSelected}
          />

          <NonSortableColumnHeader
            label={t('devices.table.columns.status')}
            minWidth="100px"
          />

          <NonSortableColumnHeader
            label={t('devices.table.columns.serialNumber')}
            minWidth="150px"
          />

          <NonSortableColumnHeader
            label={t('devices.table.columns.model')}
            minWidth="100px"
          />

          <NonSortableColumnHeader
            label={t('devices.table.columns.drillDuration')}
            minWidth="100px"
          />

          <NonSortableColumnHeader
            label={t('devices.table.columns.lastActivity')}
            minWidth="100px"
          />

          <NonSortableColumnHeader
            label={t('devices.table.columns.company')}
            minWidth="150px"
          />

         {/*  <NonSortableColumnHeader
            label={t('devices.table.columns.softwareVersion')}
            minWidth="120px"
          />
 */}
          <NonSortableColumnHeader
            label={t('devices.table.columns.warranty')}
            minWidth="100px"
          />
        </TableHeader>

        <TableBody className="divide-y divide-border">
          {devices.map((device) => (
            <tr
              key={device.id}
              className="hover:bg-surface-darker/50"
              onMouseEnter={() => setHoveredRowId(device.id)}
              onMouseLeave={() => setHoveredRowId(null)}
            >
              <CheckboxCell
                checked={selectedIds.has(device.id)}
                onChange={() => handleSelectRow(device.id)}
                ariaLabel={t('devices.table.selectRow', { serial: device.serialNumber })}
              />

              <td className="size-px whitespace-nowrap px-4 py-1">
                <StatusLabel status={device.status} />
              </td>

              <TextWithHoverAction
                value={device.serialNumber}
                isHovered={hoveredRowId === device.id}
                onCellClick={() => onRowClick(device)}
                onActionClick={() => onRowClick(device)}
                actionLabel={t('devices.table.open')}
              />

              <TextCell value={deviceTypeMap.get(Number(device.modelTypeId)) || device.modelTypeId} />

              <TextCell value={formatSecondsAsString(device.drillDuration)} />

              <TextCell value={formatLastActive(device.lastActive, t)} />

              <td className="size-px whitespace-nowrap px-4 py-1">
                {device.company && onCompanyClick ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCompanyClick(device.company!);
                    }}
                    className="text-sm text-primary hover:text-primary-darker hover:underline"
                  >
                    {device.company.name}
                  </button>
                ) : (
                  <span className="text-sm text-muted-foreground">{device.company?.name || '-'}</span>
                )}
              </td>

              {/* <td className="size-px whitespace-nowrap px-4 py-1">
                <span
                  className={`text-sm ${isOutdatedFirmware(device.firmware.currentVersion, device.firmware.newestVersion)
                    ? 'text-error'
                    : 'text-muted-foreground'
                    }`}
                >
                  {device.firmware.currentVersion}
                </span>
              </td> */}

              <TextCell value={formatDate(device.warrantyStartAt)} />
            </tr>
          ))}
        </TableBody>
      </Table>

      <TableLoadingView isLoading={isLoading} rows={devices.length === 0 ? 5 : 2} columns={8} withHeader={devices.length === 0} />
    </>
  );
}
